import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';
import { getConfig } from '@edx/frontend-platform';

export type SkillBuilderStage = 'skill_identification' | 'knowledge_map' | 'outline' | 'course' | 'unit' | 'unit_condenser_analysis' | 'unit_condenser_plan';
export type SkillBuilderJobStatus = 'queued' | 'running' | 'awaiting_approval' | 'completed' | 'failed' | 'cancel_requested' | 'cancelled';

export interface SkillBuilderArtifact {
  key: string;
  content_type: string;
}

export interface SkillBuilderJob {
  job_id: string;
  stage: SkillBuilderStage | 'source_processing';
  status: SkillBuilderJobStatus;
  progress?: string;
  error?: string;
  artifacts: Record<string, SkillBuilderArtifact>;
}

export interface StartSkillBuilderJobRequest {
  stage: SkillBuilderStage;
  input: Record<string, unknown>;
  session_id?: string;
  source_session_id?: string;
  source_ids?: string[];
  prerequisite_job_id?: string;
}

export interface SkillBuilderSourceUpload {
  source_id: string;
  job_id: string;
  status: SkillBuilderJobStatus;
}

export interface UnitBuilderSourceResult {
  id: string;
  title: string;
  snippet: string;
  url?: string;
  course_id?: string;
  sequential_id?: string;
  vertical_id?: string;
  section_type?: string;
  score?: number;
  type: 'edx_library' | 'web';
}

export interface UnitBuilderPrimarySource {
  course_id: string;
  vertical_id: string;
}

export interface SkillBuilderSourceStatus {
  source_id: string;
  job_id: string | null;
  status: SkillBuilderJobStatus | 'unavailable';
  extracted: boolean;
  filename?: string;
  total_words?: number;
  included_words?: number;
  truncated?: boolean;
}

export type UnitCondenserAssessmentImportance = 'required' | 'supporting' | 'not assessed' | 'assessment unavailable';
export type UnitCondenserPlanAction = 'keep' | 'shorten' | 'merge' | 'remove';
export type UnitCondenserPlanState = 'draft' | 'locked';
export type UnitCondenserEvidenceKind = 'assessment' | 'design_document' | 'unit_core';

export interface UnitCondenserConceptEvidence {
  kind: UnitCondenserEvidenceKind;
  label: string;
  detail: string;
  source_ids: string[];
}

export type UnitCondenserSuggestedActionKind =
  | 'remove_or_shorten'
  | 'shorten'
  | 'merge'
  | 'review_evidence'
  | 'protect_assessment';

export interface UnitCondenserSuggestedAction {
  kind: UnitCondenserSuggestedActionKind;
  label: string;
  reason: string;
  target_concept_id?: string | null;
  target_concept_title?: string | null;
}

export interface UnitCondenserConcept {
  id: string;
  title: string;
  assessment_importance: UnitCondenserAssessmentImportance;
  assessment_rationale: string;
  design_importance: string;
  action: UnitCondenserPlanAction;
  merge_target_id: string | null;
  original_words: number;
  proposed_words: number;
  rationale: string;
  source_section_ids: string[];
  author_override: string | null;
  importance_reasons?: UnitCondenserConceptEvidence[];
  suggested_actions?: UnitCondenserSuggestedAction[];
}

export interface UnitCondenserPlan {
  schema_version: number;
  analysis_job_id: string;
  source_sha256: string;
  assessment_sha256: string | null;
  revision: number;
  state: UnitCondenserPlanState;
  target_word_count: number;
  initial_target_word_count?: number | null;
  budget_override_reason: string | null;
  proposed_word_count: number;
  instructions: string;
  source_session_id: string | null;
  selected_source_ids: string[];
  source_versions: { source_id: string; sha256: string }[];
  concepts: UnitCondenserConcept[];
  locked_at: string | null;
}

export interface UnitCondenserPlanResponse {
  job_id: string;
  plan: UnitCondenserPlan;
  revisions?: UnitCondenserPlan[];
  recent_generation_context?: UnitCondenserRecentGenerationContext;
}

export interface UnitCondenserCondensationSuggestion {
  kind: 'remove_or_merge' | 'shorten_or_merge';
  concept_ids: string[];
  concept_titles: string[];
  source_section_ids: string[];
  estimated_word_savings: number;
  reason: string;
}

export interface UnitCondenserRecentGenerationContext {
  plan_revision: number;
  original_word_count: number;
  word_count: number;
  target_word_count: number;
  word_count_tolerance: number;
  word_count_variance: number;
  concepts_taught: string[];
  blocking_findings: string[];
  warnings: string[];
  structure: {
    heading_count?: number;
    section_count?: number;
    list_item_count?: number;
    table_count?: number;
    details_count?: number;
    image_placeholder_count?: number;
  };
  image_slots: {
    slot_id: string;
    position: number;
    alt_text: string;
    aspect_ratio: number | null;
    status: string;
  }[];
  condensation_suggestions: UnitCondenserCondensationSuggestion[];
}

export interface UnitCondenserDiscussionMessage {
  id: string;
  role: 'author' | 'assistant';
  content: string;
  metadata?: UnitCondenserDiscussionMetadata;
}

export type UnitCondenserGroundingStatus = 'grounded' | 'partially_grounded' | 'insufficient_evidence';
export type UnitCondenserCitationKind = 'unit_section' | 'assessment_signal' | 'supplemental_source' | 'plan';

export interface UnitCondenserDiscussionCitation {
  kind: UnitCondenserCitationKind;
  label: string;
  detail: string;
  concept_id?: string | null;
  section_id?: string | null;
  source_id?: string | null;
}

export interface UnitCondenserDiscussionMetadata {
  grounding_status: UnitCondenserGroundingStatus;
  needs_clarification: boolean;
  citations: UnitCondenserDiscussionCitation[];
  context_metadata?: {
    plan_revision?: number;
    history_messages_included?: number;
    history_truncated?: boolean;
    evidence_count?: number;
    evidence_truncated?: boolean;
    older_history_summary_included?: boolean;
    context_hash?: string;
    context_input_tokens?: number;
    context_input_budget?: number;
  };
}

export interface UnitCondenserDiscussionResponse {
  job_id: string;
  plan_revision: number;
  discussion_version: number;
  messages: UnitCondenserDiscussionMessage[];
  context_metadata?: UnitCondenserDiscussionMetadata['context_metadata'];
}

export interface UnitCondenserGenerationResult {
  schema_version: number;
  plan_job_id: string;
  plan_revision: number;
  source_sha256: string;
  assessment_sha256: string | null;
  html: string;
  original_word_count: number;
  word_count: number;
  target_word_count: number;
  concepts_taught: string[];
  sources_cited: string[];
  image_slots: {
    slot_id: string;
    position: number;
    source_section_id: string | null;
    source_package_path: string | null;
    source_path: string | null;
    alt_text: string;
    aspect_ratio: number | null;
    status: 'author_replacement_required';
    replacement_instructions: string;
  }[];
  audit: {
    word_count_tolerance: number;
    word_count_variance: number;
    blocking_findings: string[];
    warnings: string[];
    condensation_suggestions?: UnitCondenserCondensationSuggestion[];
    structure?: {
      heading_count: number;
      section_count: number;
      list_item_count: number;
      table_count: number;
      details_count: number;
      image_placeholder_count: number;
    };
    plan_revision: number;
  };
}

export interface UnitCondenserAnalysisResult {
  course_id: string;
  sequential_id: string;
  vertical_id: string;
  word_count: number;
  word_count_status: 'estimated' | 'validated';
  concepts: {
    id: string;
    title: string;
    section_index: number;
    instructional_word_count?: number;
    source_section_ids?: string[];
  }[];
  assessment_status: 'available' | 'unavailable';
  assessment_question_count: number | null;
  image_count: number;
  source_sha256: string;
}

const unitCondenserBaseUrl = () => `${getConfig().LMS_BASE_URL as string}/assistant-proxy/api/v3/unit-condensor`;
const baseUrl = () => `${getConfig().LMS_BASE_URL as string}/assistant-proxy/api/v3/skill-builder`;
const unitBuilderBaseUrl = () => `${getConfig().LMS_BASE_URL as string}/assistant-proxy/api/v3/unit-builder`;

export const analyzeUnitForCondenser = async (url: string, idempotencyKey?: string): Promise<SkillBuilderJob> => {
  const client = getAuthenticatedHttpClient();
  const requestConfig = idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined;
  const { data } = requestConfig
    ? await client.post(`${unitCondenserBaseUrl()}/analyze`, { url }, requestConfig)
    : await client.post(`${unitCondenserBaseUrl()}/analyze`, { url });
  return data as SkillBuilderJob;
};

export const analyzeUnitCondenserUnit = analyzeUnitForCondenser;

export const getUnitCondenserAnalysisJob = async (jobId: string): Promise<SkillBuilderJob> => (
  getAuthenticatedHttpClient().get(`${baseUrl()}/jobs/${encodeURIComponent(jobId)}`)
    .then(({ data }) => data as SkillBuilderJob)
);

export const getUnitCondenserAnalysisResult = async (jobId: string): Promise<UnitCondenserAnalysisResult> => {
  const { data } = await getAuthenticatedHttpClient().get(
    `${baseUrl()}/jobs/${encodeURIComponent(jobId)}/result`,
  );
  return data as UnitCondenserAnalysisResult;
};

export const createUnitCondenserPlan = async (payload: {
  analysis_job_id: string;
  expected_revision: 0;
  target_word_count: number;
  budget_override_reason?: string | null;
  instructions?: string;
  source_session_id?: string | null;
  source_ids?: string[];
}): Promise<UnitCondenserPlanResponse> => {
  const { data } = await getAuthenticatedHttpClient().post(`${unitCondenserBaseUrl()}/plans`, payload);
  return data as UnitCondenserPlanResponse;
};

export const getUnitCondenserPlan = async (planJobId: string): Promise<UnitCondenserPlanResponse> => {
  const { data } = await getAuthenticatedHttpClient().get(`${unitCondenserBaseUrl()}/plans/${encodeURIComponent(planJobId)}`);
  return data as UnitCondenserPlanResponse;
};

export const getUnitCondenserPlanForAnalysis = async (analysisJobId: string): Promise<UnitCondenserPlanResponse> => {
  const { data } = await getAuthenticatedHttpClient().get(`${unitCondenserBaseUrl()}/analyses/${encodeURIComponent(analysisJobId)}/plan`);
  return data as UnitCondenserPlanResponse;
};

export const getUnitCondenserPlanRevisions = async (
  planJobId: string,
): Promise<{ job_id: string; revisions: UnitCondenserPlan[] }> => {
  const { data } = await getAuthenticatedHttpClient().get(
    `${unitCondenserBaseUrl()}/plans/${encodeURIComponent(planJobId)}/revisions`,
  );
  return data as { job_id: string; revisions: UnitCondenserPlan[] };
};

export const getUnitCondenserDiscussion = async (
  planJobId: string,
): Promise<UnitCondenserDiscussionResponse> => {
  const { data } = await getAuthenticatedHttpClient().get(
    `${unitCondenserBaseUrl()}/plans/${encodeURIComponent(planJobId)}/discussion`,
  );
  return data as UnitCondenserDiscussionResponse;
};

export const postUnitCondenserDiscussion = async (
  planJobId: string,
  payload: {
    expected_revision: number;
    expected_discussion_version: number;
    client_message_id: string;
    message: string;
    draft_plan?: UnitCondenserPlan;
  },
): Promise<UnitCondenserDiscussionResponse> => {
  const { data } = await getAuthenticatedHttpClient().post(
    `${unitCondenserBaseUrl()}/plans/${encodeURIComponent(planJobId)}/discussion`,
    payload,
  );
  return data as UnitCondenserDiscussionResponse;
};

export const updateUnitCondenserPlan = async (
  planJobId: string,
  plan: UnitCondenserPlan,
  concepts: UnitCondenserConcept[],
): Promise<UnitCondenserPlanResponse> => {
  const proposedWordCount = concepts.reduce((sum, concept) => sum + concept.proposed_words, 0)
    || plan.target_word_count;
  const { data } = await getAuthenticatedHttpClient().put(`${unitCondenserBaseUrl()}/plans/${encodeURIComponent(planJobId)}`, {
    expected_revision: plan.revision,
    target_word_count: proposedWordCount,
    budget_override_reason: plan.budget_override_reason,
    instructions: plan.instructions,
    source_session_id: plan.source_session_id,
    source_ids: plan.selected_source_ids,
    concepts: concepts.map((concept) => ({
      id: concept.id,
      action: concept.action,
      merge_target_id: concept.merge_target_id,
      proposed_words: concept.proposed_words,
      rationale: concept.rationale,
      author_override: concept.author_override,
    })),
  });
  return data as UnitCondenserPlanResponse;
};

export const lockUnitCondenserPlan = async (
  planJobId: string,
  expectedRevision: number,
): Promise<UnitCondenserPlanResponse> => {
  const { data } = await getAuthenticatedHttpClient().post(
    `${unitCondenserBaseUrl()}/plans/${encodeURIComponent(planJobId)}/lock?expected_revision=${expectedRevision}`,
  );
  return data as UnitCondenserPlanResponse;
};

export const forkUnitCondenserPlan = async (
  planJobId: string,
  expectedRevision: number,
): Promise<UnitCondenserPlanResponse> => {
  const { data } = await getAuthenticatedHttpClient().post(
    `${unitCondenserBaseUrl()}/plans/${encodeURIComponent(planJobId)}/draft?expected_revision=${expectedRevision}`,
  );
  return data as UnitCondenserPlanResponse;
};

export const submitUnitCondenserGeneration = async (
  planJobId: string,
  expectedRevision: number,
  idempotencyKey?: string,
): Promise<SkillBuilderJob> => {
  const client = getAuthenticatedHttpClient();
  const config = idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined;
  const { data } = config
    ? await client.post(
      `${unitCondenserBaseUrl()}/plans/${encodeURIComponent(planJobId)}/generate`,
      { expected_revision: expectedRevision },
      config,
    )
    : await client.post(
      `${unitCondenserBaseUrl()}/plans/${encodeURIComponent(planJobId)}/generate`,
      { expected_revision: expectedRevision },
    );
  return data as SkillBuilderJob;
};

export const getUnitCondenserGenerationResult = async (
  jobId: string,
): Promise<UnitCondenserGenerationResult> => {
  const { data } = await getAuthenticatedHttpClient().get(
    `${baseUrl()}/jobs/${encodeURIComponent(jobId)}/result`,
  );
  return data as UnitCondenserGenerationResult;
};

export const startSkillBuilderJob = async (payload: StartSkillBuilderJobRequest): Promise<SkillBuilderJob> => {
  const { data } = await getAuthenticatedHttpClient().post(`${baseUrl()}/jobs`, payload);
  return data as SkillBuilderJob;
};

export const getSkillBuilderJob = async (jobId: string): Promise<SkillBuilderJob> => {
  const { data } = await getAuthenticatedHttpClient().get(`${baseUrl()}/jobs/${encodeURIComponent(jobId)}`);
  return data as SkillBuilderJob;
};

export const getSkillBuilderJobResult = async (jobId: string): Promise<unknown> => {
  const { data } = await getAuthenticatedHttpClient().get(`${baseUrl()}/jobs/${encodeURIComponent(jobId)}/result`);
  return data;
};

export const cancelSkillBuilderJob = async (jobId: string): Promise<SkillBuilderJob> => {
  const { data } = await getAuthenticatedHttpClient().post(`${baseUrl()}/jobs/${encodeURIComponent(jobId)}/cancel`);
  return data as SkillBuilderJob;
};

export const retrySkillBuilderJob = async (jobId: string): Promise<SkillBuilderJob> => {
  const { data } = await getAuthenticatedHttpClient().post(`${baseUrl()}/jobs/${encodeURIComponent(jobId)}/retry`);
  return data as SkillBuilderJob;
};

export const createSkillBuilderSourceSession = async (): Promise<SkillBuilderJob> => {
  const { data } = await getAuthenticatedHttpClient().post(`${baseUrl()}/source-sessions`);
  return data as SkillBuilderJob;
};

export const uploadSkillBuilderSource = async (
  sessionId: string,
  file: File,
): Promise<SkillBuilderSourceUpload> => {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await getAuthenticatedHttpClient().post(
    `${baseUrl()}/source-sessions/${encodeURIComponent(sessionId)}/files`,
    formData,
  );
  return data as SkillBuilderSourceUpload;
};

export const searchSkillBuilderUnitSources = async ({
  topic,
  useWeb = true,
  useEdx = true,
  topK = 10,
  excludeIds = [],
}: {
  topic: string;
  useWeb?: boolean;
  useEdx?: boolean;
  topK?: number;
  excludeIds?: string[];
}): Promise<{ results: UnitBuilderSourceResult[] }> => {
  const { data } = await getAuthenticatedHttpClient().post(`${unitBuilderBaseUrl()}/sources/search`, {
    topic,
    use_web: useWeb,
    use_edx: useEdx,
    top_k: topK,
    exclude_ids: excludeIds,
  });
  return data as { results: UnitBuilderSourceResult[] };
};

export const countSkillBuilderUnitSourceChunks = async (
  primarySources: UnitBuilderPrimarySource[],
): Promise<{ counts: Record<string, number>; words_per_chunk: number; primary_chunk_cap: number }> => {
  const { data } = await getAuthenticatedHttpClient().post(
    `${unitBuilderBaseUrl()}/sources/count-vertical-chunks`,
    { primary_sources: primarySources },
  );
  return data as { counts: Record<string, number>; words_per_chunk: number; primary_chunk_cap: number };
};

export const listSkillBuilderSourceSessionSources = async (
  sessionId: string,
): Promise<SkillBuilderSourceStatus[]> => {
  const { data } = await getAuthenticatedHttpClient().get(
    `${baseUrl()}/source-sessions/${encodeURIComponent(sessionId)}/sources`,
  );
  return data as SkillBuilderSourceStatus[];
};

export const downloadSkillBuilderOlxArchive = async (jobId: string): Promise<{ blob: Blob; filename: string }> => {
  const response = await getAuthenticatedHttpClient().get(
    `${baseUrl()}/jobs/${encodeURIComponent(jobId)}/olx-archive`,
    { responseType: 'blob' },
  );
  const disposition = String(response.headers?.['content-disposition'] || '');
  const filename = /filename="?([^";]+)"?/i.exec(disposition)?.[1] || 'course-builder.olx.tar.gz';
  return { blob: response.data as Blob, filename };
};
